import React from "react";
import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Slider,
  Card,
  Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useConfig } from "../../contexts/ConfigContext";
import { NAVBAR_HEIGHT } from "../../pages/Map";
import InfoIcon from "@mui/icons-material/Info";

interface AdminProps {
  onClose?: () => void;
}

const Admin: React.FC<AdminProps> = ({ onClose }) => {
  const config = useConfig();

  const handleExaggerationChange = (event: Event, newValue: number | number[]) => {
    config.updateConfig({ terrainExaggeration: newValue as number });
  };

  const opacity = Math.round((config.backgroundOpacity || 0.4) * 255)
    .toString(16)
    .padStart(2, "0");

  return (
    <Box
      sx={{
        height: "100%",
        maxHeight: `calc(100dvh - ${NAVBAR_HEIGHT})`,
        position: "fixed",
        width: "100%",
        marginTop: NAVBAR_HEIGHT,
        inset: 0,
        backgroundColor: (theme) => `${theme.palette.secondary.main}${opacity}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999999999
      }}
      tabIndex={0}
    >
      <Card
        sx={{
          width: {
            xs: "90%",
            md: "50dvw",
            lg: "35dvw"
          },
          maxWidth: "71rem", // 80% of 70rem
          minHeight: "240px", // 80% of 300px
          maxHeight: "72dvh", // 80% of 90vh
          backgroundColor: "grey.800",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          borderRadius: "0.5rem, 0.5rem, 0.5rem 0.5rem",
          borderTop: "6px solid",
          borderTopColor: "primary.main",
          transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          "&:hover": {
            boxShadow: "0 12px 48px rgba(0, 0, 0, 0.4)"
          },
          color: "#fff",
          paddingY: "24px"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingX: "24px" }}>
          <Typography variant='h2' component='h2' sx={{ fontWeight: 600 }}>
            Settings
          </Typography>
          <IconButton aria-label='close' onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant='body2' sx={{ fontStyle: "italic", color: "#fff", paddingX: "24px" }}>
          You can change options here and they should automatically update the behaviour of the application.
        </Typography>
        <Divider sx={{ backgroundColor: "grey.500", my: 16, marginX: "24px" }} />

        <Box
          sx={{ display: "flex", flexDirection: "column", gap: "24px", overflowY: "auto", height: "calc(72dvh - 120px)", paddingX: "24px" }}
        >
          {/* Related items cloud settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography variant='h5'>Related Items Cloud Settings</Typography>
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.showMediaInRelatedItemsCloud ?? false}
                  onChange={(e) => config.updateConfig({ showMediaInRelatedItemsCloud: e.target.checked })}
                  size='small'
                />
              }
              label={<Typography sx={{ margin: 0 }}>Show Media in Related Items Cloud</Typography>}
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.showGeoInRelatedItemsCloud ?? false}
                  onChange={(e) => config.updateConfig({ showGeoInRelatedItemsCloud: e.target.checked })}
                  size='small'
                />
              }
              label={<Typography sx={{ margin: 0 }}>Show Geographic Items in Related Items Cloud</Typography>}
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.showIndirectRelationshipsInRing ?? false}
                  onChange={(e) => config.updateConfig({ showIndirectRelationshipsInRing: e.target.checked })}
                  size='small'
                />
              }
              label={<Typography sx={{ margin: 0 }}>Show Secondary Relationships in Related Items Cloud</Typography>}
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
          </Box>

          {/* Data Grid Display Settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography variant='h5'>Data Grid Display Settings</Typography>
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.hideCardsWithoutImages ?? false}
                  onChange={(e) => config.updateConfig({ hideCardsWithoutImages: e.target.checked })}
                  size='small'
                />
              }
              label={
                <>
                  <Typography sx={{ margin: 0 }}>Hide Cards Without Images</Typography>
                  <Typography variant='body2' sx={{ fontStyle: "italic", color: "#fff" }}>
                    Only show cards that have primary images for a cleaner presentation
                  </Typography>
                </>
              }
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
            <FormControlLabel
              labelPlacement='start'
              control={
                <Switch
                  checked={config.limitDataDrawerToExtent ?? true}
                  onChange={(e) => config.updateConfig({ limitDataDrawerToExtent: e.target.checked })}
                  size='small'
                />
              }
              label={
                <>
                  <Typography sx={{ margin: 0 }}>Limit Data Drawer to Map Extent</Typography>
                  <Typography variant='body2' sx={{ fontStyle: "italic", color: "#fff" }}>
                    Sort items visible on the map to the top of the data drawer and grey out the rest
                  </Typography>
                </>
              }
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px"
              }}
            />
          </Box>

          {/*  3D Terrain Settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography variant='h5'> 3D Terrain Settings</Typography>
            <Box
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                flexDirection: "column",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px",
                gap: "12px"
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", margin: 0, padding: 0 }}>
                <Typography id='terrain-exaggeration-slider'>Terrain Exaggeration</Typography>
                <Slider
                  aria-labelledby='terrain-exaggeration-slider'
                  value={config.terrainExaggeration || 4}
                  onChange={handleExaggerationChange}
                  step={0.5}
                  marks
                  min={0.5}
                  max={8}
                  valueLabelDisplay='auto'
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography textAlign='end'>Exaggeration</Typography>
                <Typography textAlign='end' sx={{ backgroundColor: "grey.600", paddingX: 12, paddingY: 4, borderRadius: "4px" }}>
                  {config.terrainExaggeration || 4}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "grey.600",
                  paddingX: "12px",
                  paddingY: "6px",
                  borderRadius: "4px",
                  width: "fit-content",
                  gap: 4
                }}
              >
                <InfoIcon sx={{ marginRight: 1.5 }} />
                <Typography textAlign='end'>Adjusts vertical exaggeration of terrain in 3D view</Typography>
              </Box>
            </Box>
          </Box>

          {/*  Interface Settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography variant='h5'> Interface Settings</Typography>
            <Box
              sx={{
                width: "100%",
                color: "white",
                display: "flex",
                flexDirection: "column",
                margin: 0,
                paddingY: "16px",
                paddingX: "14px",
                backgroundColor: "grey.700",
                borderRadius: "8px",
                gap: "12px"
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", margin: 0, padding: 0 }}>
                <Typography id='terrain-exaggeration-slider'>Background Opacity</Typography>
                <Slider
                  aria-labelledby='background-opacity-slider'
                  value={config.backgroundOpacity || 0.4}
                  onChange={(event: Event, newValue: number | number[]) => config.updateConfig({ backgroundOpacity: newValue as number })}
                  step={0.01}
                  marks={[
                    { value: 0.0, label: "0%" },
                    { value: 0.2, label: "20%" },
                    { value: 0.4, label: "40%" },
                    { value: 0.6, label: "60%" },
                    { value: 0.8, label: "80%" },
                    { value: 1.0, label: "100%" }
                  ]}
                  min={0.0}
                  max={1.0}
                  valueLabelDisplay='auto'
                  valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                  sx={{
                    "& .MuiSlider-markLabel": {
                      color: "#fff"
                    }
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography textAlign='end'>Opacity</Typography>
                <Typography textAlign='end' sx={{ backgroundColor: "grey.600", paddingX: 12, paddingY: 4, borderRadius: "4px" }}>
                  {Math.round((config.backgroundOpacity || 0.4) * 100)}%
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "grey.600",
                  paddingX: "12px",
                  paddingY: "6px",
                  borderRadius: "4px",
                  width: "fit-content",
                  gap: 4
                }}
              >
                <InfoIcon />
                <Typography textAlign='end'>Controls how much the page background overlays the map</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default Admin;
